// ============================================================
// TALENT CATALOG - Data-driven registry of all talent definitions.
// Each talent has a unique ID, display name, description, category,
// and an array of TalentEffect descriptors that the resolver interprets.
// ============================================================
import type { TalentEffect } from './talentEffects';

export interface TalentDefinition {
  id: string;
  name: string;
  description: string;
  category: 'combat' | 'defence' | 'general' | 'narrative';
  parameterized: boolean;
  parameterLabel?: string;
  /**
   * May be taken more than once, each with its own parameter. Left off
   * for entries whose effects are SUMMED or read first-only, where a
   * second copy would double a number or be silently ignored.
   */
  repeatable?: boolean;
  effects: TalentEffect[];
}

/**
 * TALENT_CATALOG - Core Rulebook talent definitions.
 * Keyed by talent ID for O(1) lookup.
 */
export const TALENT_CATALOG: Record<string, TalentDefinition> = {
  air_of_authority: {
    id: 'air_of_authority',
    name: 'Air of Authority',
    description: 'Affect 10× more people with Command tests.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Command tests affect 10× more people.' }],
  },

  ambidextrous: {
    id: 'ambidextrous',
    name: 'Ambidextrous',
    description: 'No off-hand penalty when using two weapons.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'No off-hand penalty when dual-wielding.' }],
  },

  assassin_strike: {
    id: 'assassin_strike',
    name: 'Assassin Strike',
    description: 'After a melee attack, may make a free Half Move.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Free Half Move after melee attack.' }],
  },

  astartes_weapon_training: {
    id: 'astartes_weapon_training',
    name: 'Astartes Weapon Training',
    description: 'Proficient in ALL weapon groups except Exotic weapons.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Proficient in all weapon groups except Exotic.' }],
  },

  battle_rage: {
    id: 'battle_rage',
    name: 'Battle Rage',
    description: 'May Parry while Frenzied.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'May Parry while Frenzied. (Mechanically enforced by resolver.)' }],
  },

  berserk_charge: {
    id: 'berserk_charge',
    name: 'Berserk Charge',
    description: '+20 WS on Charge instead of +10.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'narrative', description: '+20 WS on Charge instead of +10.' }],
  },

  blademaster: {
    id: 'blademaster',
    name: 'Blademaster',
    description: 'May re-roll one missed melee attack per round.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'reroll_grant', test: 'melee_attack', against: 'miss' }],
  },

  bulging_biceps: {
    id: 'bulging_biceps',
    name: 'Bulging Biceps',
    description: 'May fire heavy weapons using Semi-Auto or Full Auto Burst without bracing.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Fire heavy weapons on Semi/Full Auto Burst without bracing.' }],
  },

  catfall: {
    id: 'catfall',
    name: 'Catfall',
    description: 'Reduce falling distance by Ag bonus for damage purposes. Always lands on feet.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Reduce fall damage by Ag bonus metres.' }],
  },

  combat_formation: {
    id: 'combat_formation',
    name: 'Combat Formation',
    description: 'Allies within 10m gain +10 to Initiative.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Allies within 10m get +10 Initiative.' }],
  },

  combat_master: {
    id: 'combat_master',
    name: 'Combat Master',
    description: 'Opponents get no gang-up bonus in melee.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Opponents get no gang-up bonus.' }],
  },

  counter_attack: {
    id: 'counter_attack',
    name: 'Counter-Attack',
    description: 'After a successful Parry, may make a free Standard Attack against that attacker.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Free attack after successful parry.' }],
  },

  crack_shot: {
    id: 'crack_shot',
    name: 'Crack Shot',
    description: '+2 critical damage on ranged attacks.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'critical_bonus', value: 2, scope: 'ranged' }],
  },

  crippling_strike: {
    id: 'crippling_strike',
    name: 'Crippling Strike',
    description: '+2 critical damage on melee attacks.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'critical_bonus', value: 2, scope: 'melee' }],
  },

  crushing_blow: {
    id: 'crushing_blow',
    name: 'Crushing Blow',
    description: '+2 damage to melee attacks.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'damage_bonus', value: 2, scope: 'melee' }],
  },

  deadeye_shot: {
    id: 'deadeye_shot',
    name: 'Deadeye Shot',
    description: 'Reduce called shot penalty by -10 on ranged attacks.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'called_shot_reduction', value: 10, scope: 'ranged' }],
  },

  deathwatch_training: {
    id: 'deathwatch_training',
    name: 'Deathwatch Training',
    description: 'Automatically confirms Righteous Fury when attacking Xenos targets.',
    category: 'combat',
    parameterized: false,
    effects: [
      {
        type: 'narrative',
        description: 'Righteous Fury auto-confirms vs Xenos. (Mechanically enforced when the target is flagged Xenos.)',
      },
    ],
  },

  deflect_shot: {
    id: 'deflect_shot',
    name: 'Deflect Shot',
    description: 'May parry ranged attacks.',
    category: 'defence',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'May parry ranged attacks. (Mechanically enforced by resolver.)' }],
  },

  die_hard: {
    id: 'die_hard',
    name: 'Die Hard',
    description: '+WP bonus to tests to avoid death from critical damage.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'threshold_modifier', test: 'death', bonus: 0 }], // bonus derived from WP at runtime
  },

  disarm: {
    id: 'disarm',
    name: 'Disarm',
    description: 'May disarm opponent with an Opposed WS Test.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Disarm via Opposed WS Test.' }],
  },

  double_team: {
    id: 'double_team',
    name: 'Double Team',
    description: '+10 WS when ganging up with an ally.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: '+10 WS when ganging up.' }],
  },

  dual_shot: {
    id: 'dual_shot',
    name: 'Dual Shot',
    description: 'Fire two single shots from two pistols as a Full Action.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Two single shots from two pistols (Full Action).' }],
  },

  duty_unto_death: {
    id: 'duty_unto_death',
    name: 'Duty Unto Death',
    description: 'May continue fighting for 1 round after receiving a killing blow.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Continue fighting for 1 round after lethal blow.' }],
  },

  eye_of_vengeance: {
    id: 'eye_of_vengeance',
    name: 'Eye of Vengeance',
    description: 'Spend Fate Point to add Degrees of Success to ranged damage.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Spend Fate to add DoS to ranged damage.' }],
  },

  favoured_by_the_warp: {
    id: 'favoured_by_the_warp',
    name: 'Favoured by the Warp',
    description: 'Roll twice on Psychic Phenomena and choose result.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Roll twice on Psychic Phenomena, choose result.' }],
  },

  fearless: {
    id: 'fearless',
    name: 'Fearless',
    description: 'Immune to Fear and Pinning.',
    category: 'narrative',
    parameterized: false,
    // The condition codes were 'F' and 'P' - Fatigue and Prone - and
    // nothing ever read them. Pinning is 'Pn'; Fear has no condition
    // code, it is a VECTOR the resistance engine resists (v8.346.0).
    effects: [
      { type: 'condition_immunity', conditions: ['Pn'] },
      { type: 'vector_resistance', vector: 'fear', mode: 'immune', label: 'Fearless' },
    ],
  },

  flesh_render: {
    id: 'flesh_render',
    name: 'Flesh Render',
    description: '+2 critical damage on all attacks.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'critical_bonus', value: 2, scope: 'all' }],
  },

  frenzy: {
    id: 'frenzy',
    name: 'Frenzy',
    description:
      'While Frenzied: +10 WS, immune to Stunning, Fear, Pinning, and the effects of fatigue. Cannot Parry or retreat.',
    category: 'combat',
    parameterized: false,
    effects: [
      { type: 'skill_bonus', value: 10, skill: 'ws', condition: 'frenzied' },
      { type: 'condition_immunity', conditions: ['S'], condition: 'frenzied' },
    ],
  },

  furious_assault: {
    id: 'furious_assault',
    name: 'Furious Assault',
    description: 'After making All Out Attack, may spend Reaction to make second attack.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Spend Reaction for second All Out Attack.' }],
  },

  hammer_blow: {
    id: 'hammer_blow',
    name: 'Hammer Blow',
    description: '+2 damage and +2 penetration on All Out Attack.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'narrative', description: '+2 damage and +2 penetration on All Out Attack.' }],
  },

  hardy: {
    id: 'hardy',
    name: 'Hardy',
    description: 'Immune to Blood Loss from critical damage.',
    category: 'defence',
    parameterized: false,
    effects: [{ type: 'condition_immunity', conditions: ['BL', 'BLS'], source: 'crits' }],
  },

  hatred: {
    id: 'hatred',
    name: 'Hatred',
    description: '+10 WS against hated enemy; must re-roll missed attacks in first round.',
    category: 'combat',
    parameterized: true,
    repeatable: true,
    parameterLabel: 'Enemy (e.g. Orks, Tyranids)',
    effects: [
      { type: 'skill_bonus', skill: 'ws', value: 10, condition: 'hatred' },
      { type: 'narrative', description: 'Must re-roll missed attacks in round 1 vs {param}.' },
    ],
  },

  heightened_senses: {
    id: 'heightened_senses',
    name: 'Heightened Senses',
    description:
      '+10 bonus to Tests involving the chosen sense (e.g. Sight or Sound). Stacks with the Auto-Senses Power Armour ability.',
    category: 'general',
    parameterized: true,
    repeatable: true,
    parameterLabel: 'Sense (e.g. Sight, Hearing)',
    effects: [
      // +10 to Perception-based skill tests (Awareness, Scrutiny, Search, Psyniscience).
      // Multiple instances (Sight + Hearing) do NOT stack - collectSkillBonuses
      // takes one bonus per talent id.
      { type: 'skill_bonus', value: 10, governing: 'per' },
      { type: 'narrative', description: '+10 to Tests involving {param}. Stacks with Auto-Senses.' },
    ],
  },

  hip_shooting: {
    id: 'hip_shooting',
    name: 'Hip Shooting',
    description: 'May shoot while making a Half Move action.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Shoot while making a Half Move.' }],
  },

  hunter_of_aliens: {
    id: 'hunter_of_aliens',
    name: 'Hunter of Aliens',
    description: 'May re-roll damage against xenos targets.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'reroll_grant', test: 'damage', against: 'xenos' }],
  },

  independent_targeting: {
    id: 'independent_targeting',
    name: 'Independent Targeting',
    description: 'When dual-wielding, may fire at different targets.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'May fire each weapon at a different target.' }],
  },

  iron_jaw: {
    id: 'iron_jaw',
    name: 'Iron Jaw',
    description: 'Test Toughness to resist being Stunned (replaces automatic stun).',
    category: 'defence',
    parameterized: false,
    effects: [{ type: 'threshold_modifier', test: 'toughness_vs_stun', bonus: 0 }],
  },

  jaded: {
    id: 'jaded',
    name: 'Jaded',
    description: 'Immune to mundane horrors (Fear from non-Warp sources of rating 1).',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Immune to mundane Fear (rating 1, non-Warp).' }],
  },

  killing_strike: {
    id: 'killing_strike',
    name: 'Killing Strike',
    description: 'Spend a Fate Point when making a melee All Out Attack: the attack cannot be Dodged or Parried.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Spend Fate: melee All Out Attack cannot be Dodged or Parried.' }],
  },

  leap_up: {
    id: 'leap_up',
    name: 'Leap Up',
    description: 'Stand from Prone as a Free Action.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Stand from Prone as Free Action.' }],
  },

  light_sleeper: {
    id: 'light_sleeper',
    name: 'Light Sleeper',
    description: 'Always count as awake for Awareness tests, even while sleeping.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Always awake for Awareness tests.' }],
  },

  lightning_attack: {
    id: 'lightning_attack',
    name: 'Lightning Attack',
    description: 'May make a Lightning Attack action (full-auto in melee).',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'attack_mode_unlock', mode: 'lightning' }],
  },

  marksman: {
    id: 'marksman',
    name: 'Marksman',
    description: 'No penalty for shooting at long range.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'range_modifier', removeLongRangePenalty: true, removeExtremeRangePenalty: false }],
  },

  mental_fortress: {
    id: 'mental_fortress',
    name: 'Mental Fortress',
    description: 'Immune to mind-affecting psychic powers.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Immune to psychic mind control.' }],
  },

  mighty_shot: {
    id: 'mighty_shot',
    name: 'Mighty Shot',
    description: '+2 damage to ranged attacks.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'damage_bonus', value: 2, scope: 'ranged' }],
  },

  nerves_of_steel: {
    id: 'nerves_of_steel',
    name: 'Nerves of Steel',
    description: 'May re-roll failed Willpower Tests to avoid or recover from Pinning (suppressive fire).',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'reroll_grant', test: 'willpower', against: 'pinning', label: 'Nerves of Steel' }],
  },

  // ── General talents (mechanical) ─────────────────────────,

  peer: {
    id: 'peer',
    name: 'Peer',
    description: '+10 to Fellowship tests with a chosen group.',
    category: 'narrative',
    parameterized: true,
    repeatable: true,
    parameterLabel: 'Group (e.g. Imperial Guard, Mechanicus)',
    effects: [{ type: 'narrative', description: '+10 Fellowship with specified group.' }],
  },

  precise_blow: {
    id: 'precise_blow',
    name: 'Precise Blow',
    description: 'Reduce called shot penalty by -10 on melee attacks.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'called_shot_reduction', value: 10, scope: 'melee' }],
  },

  preternatural_speed: {
    id: 'preternatural_speed',
    name: 'Preternatural Speed',
    description: '+2 to Initiative rolls.',
    // Mechanical since v8.67.0. It had described itself correctly and
    // done nothing for as long as initiative was a number you typed.
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'initiative_modifier', value: 2 }],
  },

  purity_of_faith: {
    id: 'purity_of_faith',
    name: 'Purity of Faith',
    description: 'Spend Fate Point to automatically resist a psychic power.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Spend Fate to auto-resist psychic power.' }],
  },

  quick_draw: {
    id: 'quick_draw',
    name: 'Quick Draw',
    description:
      'May Ready as a Free Action when armed with a pistol or basic ranged weapon, or a one-handed melee weapon.',
    category: 'narrative',
    parameterized: false,
    effects: [
      { type: 'narrative', description: 'Ready pistols, basic ranged, or one-hand melee weapons as a Free Action.' },
    ],
  },

  rapid_reload: {
    id: 'rapid_reload',
    name: 'Rapid Reload',
    description: 'Halve reload time (Full → Half, Half → Free).',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Reload time halved.' }],
  },

  resistance: {
    id: 'resistance',
    name: 'Resistance',
    description: '+10 to resist tests against a specific effect.',
    category: 'narrative',
    parameterized: true,
    repeatable: true,
    parameterLabel: 'Type (e.g. Fear, Psychic Powers)',
    effects: [{ type: 'resistance', value: 10, against: '' }],
  },

  rite_of_sanctioning: {
    id: 'rite_of_sanctioning',
    name: 'Rite of Sanctioning',
    description: 'Sanctioned psyker - less likely to cause Perils of the Warp.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Sanctioned: reduced Perils of the Warp risk.' }],
  },

  scourge_of_heretics: {
    id: 'scourge_of_heretics',
    name: 'Scourge of Heretics',
    description: 'May re-roll damage against heretics and witches.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'reroll_grant', test: 'damage', against: 'heretics' }],
  },

  signature_wargear_master: {
    id: 'signature_wargear_master',
    name: 'Signature Wargear (Master)',
    description: 'A single weapon of exceptional quality.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Exceptional quality signature weapon.' }],
  },

  sound_constitution: {
    id: 'sound_constitution',
    name: 'Sound Constitution',
    description: '+1 Wound per rank of this talent.',
    category: 'general',
    parameterized: true,
    parameterLabel: 'Ranks',
    effects: [{ type: 'wound_bonus', value: 1 }],
  },

  sprint: {
    id: 'sprint',
    name: 'Sprint',
    description: 'Triple (instead of double) movement when Running.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Triple movement when Running.' }],
  },

  step_aside: {
    id: 'step_aside',
    name: 'Step Aside',
    description: 'May make an additional Dodge reaction per round.',
    category: 'defence',
    parameterized: false,
    effects: [{ type: 'defence_grant', defence: 'dodge', additional: 1 }],
  },

  strong_minded: {
    id: 'strong_minded',
    name: 'Strong Minded',
    description: 'May re-roll failed Willpower tests.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'reroll_grant', test: 'willpower', against: 'all' }],
  },

  // ── General / Narrative talents ──────────────────────────,

  sure_strike: {
    id: 'sure_strike',
    name: 'Sure Strike',
    description: 'When making a Called Shot melee attack, may re-roll a missed attack.',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'reroll_grant', test: 'called_shot_melee_attack', against: 'miss' }],
  },

  swift_attack: {
    id: 'swift_attack',
    name: 'Swift Attack',
    description: 'May make a Swift Attack action (semi-auto in melee).',
    category: 'combat',
    parameterized: false,
    effects: [{ type: 'attack_mode_unlock', mode: 'swift' }],
  },

  tactical_expertise: {
    id: 'tactical_expertise',
    name: 'Tactical Expertise',
    description: 'Allies within 20m may re-roll failed Initiative tests.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Allies within 20m re-roll Initiative.' }],
  },

  talented: {
    id: 'talented',
    name: 'Talented',
    description: '+10 bonus to a chosen skill.',
    category: 'narrative',
    parameterized: true,
    repeatable: true,
    parameterLabel: 'Skill (e.g. Dodge, Awareness)',
    effects: [{ type: 'narrative', description: '+10 to parameterized skill.' }],
  },

  target_selection: {
    id: 'target_selection',
    name: 'Target Selection',
    description: 'Shoot into melee without hitting allies.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'No risk of hitting allies when shooting into melee.' }],
  },

  thunder_charge: {
    id: 'thunder_charge',
    name: 'Thunder Charge',
    description: 'When Charging, knock opponents aside. Opponents must pass Strength test or be knocked Prone.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Knock opponents Prone when Charging.' }],
  },

  total_recall: {
    id: 'total_recall',
    name: 'Total Recall',
    description: 'Automatically remember any trivial fact or data.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Perfect memory recall.' }],
  },

  touched_by_the_fates: {
    id: 'touched_by_the_fates',
    name: 'Touched by the Fates',
    description: 'Begin each session with one additional Fate Point.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: '+1 starting Fate Point per session.' }],
  },

  true_grit: {
    id: 'true_grit',
    name: 'True Grit',
    description: 'Halve critical damage severity (round up, minimum 1).',
    category: 'defence',
    parameterized: false,
    effects: [{ type: 'crit_reduction', factor: 0.5 }],
  },

  two_weapon_wielder: {
    id: 'two_weapon_wielder',
    name: 'Two-Weapon Wielder',
    description: 'May attack with both weapons as a Full Action with a -20 penalty to each.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'May dual-wield with -20 penalty to each attack.' }],
  },

  // ── Combat talents (mechanical) ──────────────────────────,

  unarmed_master: {
    id: 'unarmed_master',
    name: 'Unarmed Master',
    description: 'Unarmed attacks deal 1d10+SB I damage and do not count as Primitive.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Unarmed attacks deal 1d10+SB I, not Primitive.' }],
  },

  unarmed_warrior: {
    id: 'unarmed_warrior',
    name: 'Unarmed Warrior',
    description: 'Unarmed attacks deal 1d5+SB I damage.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Unarmed attacks deal 1d5+SB I damage.' }],
  },

  // ── Combat talents (narrative-only) ──────────────────────,

  unshakeable_faith: {
    id: 'unshakeable_faith',
    name: 'Unshakeable Faith',
    description: 'Immune to Fear and Pinning caused by daemons.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Immune to daemonic Fear and Pinning.' }],
  },

  wall_of_steel: {
    id: 'wall_of_steel',
    name: 'Wall of Steel',
    description: 'May make an additional Parry reaction per round.',
    category: 'defence',
    parameterized: false,
    effects: [{ type: 'defence_grant', defence: 'parry', additional: 1 }],
  },

  warp_conduit: {
    id: 'warp_conduit',
    name: 'Warp Conduit',
    description: 'Spend Fate Point to add +1d5 to Psy Rating for one power.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Spend Fate for +1d5 Psy Rating on one power.' }],
  },

  whirlwind_of_death: {
    id: 'whirlwind_of_death',
    name: 'Whirlwind of Death',
    description: 'May attack all engaged opponents as a Full Action.',
    category: 'narrative',
    parameterized: false,
    effects: [{ type: 'narrative', description: 'Attack all engaged opponents (Full Action).' }],
  },

  // ── Defence talents (mechanical) ─────────────────────────,
};

/** Ordered list of all talent IDs for UI iteration */
export const TALENT_IDS = Object.keys(TALENT_CATALOG);

/** Get a talent definition by ID, or undefined if not found */
export function getTalent(id: string): TalentDefinition | undefined {
  return TALENT_CATALOG[id];
}
